import { ReactNode, useCallback, useMemo, useState, useEffect, useRef } from "react";
import {
  AppBar,
  Badge,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import GridViewIcon from "@mui/icons-material/GridView";
import SensorsIcon from '@mui/icons-material/Sensors';
import FlightIcon from '@mui/icons-material/Flight';
import EditNoteIcon from "@mui/icons-material/EditNote";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import BugReportIcon from "@mui/icons-material/BugReport";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BarChartIcon from "@mui/icons-material/BarChart";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import FiberSmartRecordIcon from "@mui/icons-material/FiberSmartRecord";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import { useThemePreference } from "../context/ThemePreferenceContext";
import { useServerVersion } from "../hooks/useServerVersion";
import { PwaInstallButton } from "./PwaInstallButton";
import { isDesktopShellEnvironment } from "../utils/desktopShell";
import impleoLogoUrl from "../assets/Impleo_logo.png";

const drawerWidth = 220;

type NavigationItem = {
  label: string;
  icon: ReactNode;
  path: string;
  external?: boolean;
  toggleDarkMode?: boolean;
};

type AppLayoutProps = {
  children: ReactNode;
};

const navigationItems: NavigationItem[] = [
  { label: "Player", icon: <LiveTvIcon />, path: "/player" },
  { label: "Mosaic", icon: <GridViewIcon />, path: "/mosaic" },
  { label: "Streams", icon: <SensorsIcon />, path: "/streams" },
  { label: "Recordings", icon: <FiberSmartRecordIcon />, path: "/recordings" },
  { label: "Platforms", icon: <FlightIcon />, path: "/platforms" },
  { label: "Annotations", icon: <EditNoteIcon />, path: "/annotations" },
  { label: "Users", icon: <PeopleIcon />, path: "/users" },
  { label: "User groups", icon: <GroupAddIcon />, path: "/usergroups" },
  { label: "Settings", icon: <SettingsIcon />, path: "/config" },
  { label: "Preferences", icon: <ManageAccountsIcon />, path: "/preferences" },
  { label: "System Monitor", icon: <BarChartIcon />, path: "/system-monitor" },
  { label: "WebRtc Debug", icon: <BugReportIcon />, path: "/debug/whep" },
  { label: "Login", icon: <LoginIcon />, path: "/login" },
  { label: "Register", icon: <AppRegistrationIcon />, path: "/register" },
  { label: "Logout", icon: <LogoutIcon />, path: "/logout" },
  { label: "Logs", icon: <ReceiptLongIcon />, path: "/logs" },
  { label: "Dark mode", icon: <DarkModeIcon />, path: "#dark-mode", toggleDarkMode: true },
  { label: "Help", icon: <HelpOutlineIcon />, path: "/help/index.html", external: true }
];

const DRAWER_STATE_KEY = "skyview.drawer.open";

const AppLayout = ({ children }: AppLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return true;
    }

    const stored = window.localStorage.getItem(DRAWER_STATE_KEY);
    return stored === null ? true : stored === "true";
  });
  const [isStandalone, setIsStandalone] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userManagementEnabled } = useAuth();
  const { connectedClients, eventStreamConnected, serverConfig, isDemoMode } = useAppContext();
  const [showDisconnectOverlay, setShowDisconnectOverlay] = useState(false);
  const overlayTimerRef = useRef<number | null>(null);
  const { darkThemeEnabled, setDarkThemeEnabled } = useThemePreference();
  const { versionInfo, versionLabel } = useServerVersion();
  const isDesktopShell = useMemo(() => isDesktopShellEnvironment(), []);
  const desktopBackendOriginRef = useRef<string | null>(null);
  const versionText = versionInfo?.version ?? versionLabel;
  const isUserManagementEnabled = useMemo(
    () => userManagementEnabled && serverConfig.userManagementEnabled,
    [serverConfig.userManagementEnabled, userManagementEnabled]
  );
  const filteredNavigation = useMemo(() => {
    return navigationItems.filter((item) => {
      if (!isUserManagementEnabled) {
        switch (item.path) {
          case "/login":
          case "/register":
          case "/logout":
          case "/users":
          case "/usergroups":
            return false;
          default:
            break;
        }
      }

      switch (item.path) {
        case "/logout":
          return Boolean(user);
        case "/login":
        case "/register":
          return !user;
        case "/config":
          return user?.role === "admin";
        case "/users":
        case "/usergroups":
        case "/logs":
          return user?.role === "admin";
        case "/recordings":
          return !isDemoMode && user?.role === "admin" && serverConfig.recastLicensed && serverConfig.recordLicensed;
        case "/annotations":
          return Boolean(user);
        case "/streams":
        case "/platforms":
          return Boolean(user);
        case "/mosaic":
          return Boolean(user) && serverConfig.enablePreview;
        case "/system-monitor":
          return user?.role === "admin" && serverConfig.enableSystemMonitor;
        case "/debug/whep":
          return Boolean(user) && serverConfig.enableWebRtcDebug;
        default:
          return true;
      }
    });
  }, [user, isUserManagementEnabled, isDemoMode, serverConfig.enablePreview, serverConfig.enableSystemMonitor, serverConfig.enableWebRtcDebug, serverConfig.recastLicensed, serverConfig.recordLicensed]);

  // Persist desktop drawer state in localStorage when it changes
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(DRAWER_STATE_KEY, String(desktopOpen));
  }, [desktopOpen]);

  useEffect(() => {
    if (!isUserManagementEnabled || user) {
      return;
    }

    if (location.pathname === "/login" || location.pathname === "/register" || location.pathname === "/password-reset") {
      return;
    }

    navigate("/login", { replace: true });
  }, [isUserManagementEnabled, user, location.pathname, navigate]);

  const handleDrawerToggle = () => {
    if (isSmallScreen) {
      setMobileOpen((prev) => !prev);
    } else {
      setDesktopOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;

    const evaluateStandalone = () => {
      const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
      setIsStandalone(Boolean(mediaQuery?.matches) || iosStandalone);
    };

    evaluateStandalone();

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", evaluateStandalone);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(evaluateStandalone);
      }
    }

    const handleAppInstalled = () => evaluateStandalone();
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === "function") {
          mediaQuery.removeEventListener("change", evaluateStandalone);
        } else if (typeof mediaQuery.removeListener === "function") {
          mediaQuery.removeListener(evaluateStandalone);
        }
      }
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (overlayTimerRef.current !== null) {
      window.clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }

    if (eventStreamConnected) {
      setShowDisconnectOverlay(false);
      return;
    }

    overlayTimerRef.current = window.setTimeout(() => {
      overlayTimerRef.current = null;
      setShowDisconnectOverlay(true);
    }, 600);

    return () => {
      if (overlayTimerRef.current !== null) {
        window.clearTimeout(overlayTimerRef.current);
        overlayTimerRef.current = null;
      }
    };
  }, [eventStreamConnected]);

  const closeOnNavigation = useCallback(() => {
    if (isSmallScreen) {
      setMobileOpen(false);
    } else if (!desktopOpen) {
      setDesktopOpen(true);
    }
  }, [isSmallScreen, desktopOpen]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const resolveDesktopExternalOrigin = useCallback(async (): Promise<string | null> => {
    if (!isDesktopShell) {
      return null;
    }
    if (desktopBackendOriginRef.current) {
      return desktopBackendOriginRef.current;
    }

    try {
      const response = await fetch("/__desktop_meta", { cache: "no-store" });
      if (!response.ok) {
        return null;
      }
      const payload = (await response.json().catch(() => null)) as { backendAddr?: unknown } | null;
      const backendAddr = typeof payload?.backendAddr === "string" ? payload.backendAddr.trim() : "";
      if (!backendAddr) {
        return null;
      }

      const origin = `http://${backendAddr}`;
      desktopBackendOriginRef.current = origin;
      return origin;
    } catch {
      return null;
    }
  }, [isDesktopShell]);

  const openExternalPath = useCallback(async (path: string) => {
    const isAbsolute = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(path);
    const desktopOrigin = await resolveDesktopExternalOrigin();
    const fallbackOrigin = window.location.origin;
    const baseOrigin = desktopOrigin ?? fallbackOrigin;
    const targetUrl = isAbsolute ? path : new URL(path, `${baseOrigin}/`).toString();

    const runtime = (window as { runtime?: { BrowserOpenURL?: (url: string) => void } }).runtime;
    if (isDesktopShell && typeof runtime?.BrowserOpenURL === "function") {
      runtime.BrowserOpenURL(targetUrl);
      return;
    }

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }, [isDesktopShell, resolveDesktopExternalOrigin]);

  const drawer = (
    <Box role="presentation" sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Toolbar
        variant="dense"
        sx={{ minHeight: 48, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          component="img"
          src={impleoLogoUrl}
          alt="Impleo"
          sx={{ height: 28, display: "block" }}
        />
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto" }}>
        {filteredNavigation.map((item) => {
          if (item.toggleDarkMode) {
            return (
              <ListItemButton
                key={item.path}
                onClick={() => {
                  setDarkThemeEnabled(!darkThemeEnabled);
                  closeOnNavigation();
                }}
                aria-pressed={darkThemeEnabled}
              >
                <ListItemIcon>
                  {darkThemeEnabled ? (
                    <DarkModeIcon />
                  ) : (
                    <LightModeIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={darkThemeEnabled ? "Dark Theme" : "Light Theme"}
                />
              </ListItemButton>
            );
          }
          const selected = !item.external && location.pathname === item.path;
          const component = item.external ? "a" : Link;
          return (
            <ListItemButton
              key={item.path}
              component={component}
              {...(item.external
                ? {
                  href: item.path,
                  target: "_blank",
                  rel: "noopener noreferrer"
                }
                : { to: item.path })}
              selected={selected}
              onClick={(event) => {
                closeOnNavigation();
                if (!item.external) {
                  return;
                }

                event.preventDefault();
                void openExternalPath(item.path);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0
        }}
      >
        <a
          href="https://www.impleotv.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: (theme) => theme.palette.primary.main }}
          >
            IMPLEOTV SYSTEMS LTD
          </Typography>
        </a>
        {versionText && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              color: (theme) => theme.palette.text.secondary,
              textAlign: "center",
              wordBreak: "break-word"
            }}
          >
            {versionText}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ml: { sm: desktopOpen ? `${drawerWidth}px` : 0 },
          width: { sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : "100%" }
        }}
      >
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            SkyView
          </Typography>
          <PwaInstallButton />
          {isStandalone && (
            <Tooltip title="Reload" placement="bottom">
              <IconButton color="inherit" aria-label="reload" onClick={handleReload}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title={eventStreamConnected ? "Connected" : "Disconnected"} placement="bottom">
              <Box
                component="span"
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: eventStreamConnected ? "success.main" : "text.disabled",
                  border: (theme) => `1px solid ${theme.palette.background.paper}`,
                }}
              />
            </Tooltip>
            <Tooltip
              title={eventStreamConnected ? "Connected clients" : "Client count unavailable"}
              placement="bottom"
            >
              {eventStreamConnected ? (
                <Badge
                  color="secondary"
                  badgeContent={connectedClients}
                  showZero
                  overlap="circular"
                  sx={{ "& .MuiBadge-badge": { minWidth: 18, height: 18, fontSize: "0.75rem" } }}
                >
                  <GroupIcon fontSize="small" />
                </Badge>
              ) : (
                <GroupIcon fontSize="small" sx={{ color: "text.disabled" }} />
              )}
            </Tooltip>
          </Box>
          {user && isUserManagementEnabled && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Tooltip title={"Username"} placement="bottom">
                <Typography variant="body1" fontWeight={600} sx={{ color: "text.secondary" }}>
                  {user.username}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: desktopOpen ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              position: "relative",
              height: "100vh"
            }
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 1,
          pb: 3,
          px: { xs: 1, sm: 3 },
          width: {
            xs: "100%",
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : "100%"
          },
          position: "relative"
        }}
      >
        <Toolbar />
        {children}
        {showDisconnectOverlay && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0, 0, 0, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: (theme) => theme.zIndex.appBar - 1
            }}
          >
            <CircularProgress size={64} thickness={4} color="inherit" />
          </Box>
        )}
        {isDemoMode && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: { xs: 0, sm: desktopOpen ? drawerWidth : 0 },
              right: 0,
              color: "warning.main",
              py: 0.5,
              px: 2,
              textAlign: "center",
              zIndex: (theme) => theme.zIndex.appBar - 1,
              fontWeight: 600,
              fontSize: "0.875rem"
            }}
          >
            <Typography variant="overline">
              Demo Mode
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AppLayout;
